import type {
  AdvertisingCostData,
  AdvertisingDailyRecord,
  AdvertisingSourceAmount,
} from './types'

export interface AdvertisingSummary {
  requestedDays: number
  coveredDays: number
  complete: boolean
  regularJztCoveredDays: number
  brandContractCoveredDays: number
  jdAllianceCoveredDays: number
  regularJzt: number
  brandContract: number
  jdAlliance: number
  total: number
  provisionalDays: number
}

export interface AdvertisingProfitSummary extends AdvertisingSummary {
  gmv: number
  profitBeforeAdvertising: number | null
  profitAfterAdvertising: number | null
  advertisingRate: number | null
  profitAfterAdvertisingMargin: number | null
}

export const advertisingRecordTotal = (record: AdvertisingDailyRecord) =>
  record.regularJzt.amount + record.brandContract.amount + record.jdAlliance.amount

export const advertisingSourceAvailable = (source: AdvertisingSourceAmount) =>
  source.status !== 'missing'

export const advertisingRecordComplete = (record: AdvertisingDailyRecord) =>
  advertisingSourceAvailable(record.regularJzt)
  && advertisingSourceAvailable(record.brandContract)
  && advertisingSourceAvailable(record.jdAlliance)

export function advertisingRecordsByDate(data: AdvertisingCostData | null) {
  return new Map((data?.dailyRecords || []).map((record) => [record.date, record]))
}

const isProvisional = (source: AdvertisingSourceAmount) => source.status === 'provisional'

export function summarizeAdvertising(
  data: AdvertisingCostData | null,
  dates: string[],
): AdvertisingSummary {
  const index = advertisingRecordsByDate(data)
  const records = dates.map((date) => index.get(date)).filter(Boolean) as AdvertisingDailyRecord[]
  const completeRecords = records.filter(advertisingRecordComplete)
  return {
    requestedDays: dates.length,
    coveredDays: completeRecords.length,
    complete: dates.length > 0 && completeRecords.length === dates.length,
    regularJztCoveredDays: records.filter((record) =>
      advertisingSourceAvailable(record.regularJzt)).length,
    brandContractCoveredDays: records.filter((record) =>
      advertisingSourceAvailable(record.brandContract)).length,
    jdAllianceCoveredDays: records.filter((record) =>
      advertisingSourceAvailable(record.jdAlliance)).length,
    regularJzt: records.reduce((sum, record) =>
      sum + (advertisingSourceAvailable(record.regularJzt) ? record.regularJzt.amount : 0), 0),
    brandContract: records.reduce((sum, record) =>
      sum + (advertisingSourceAvailable(record.brandContract) ? record.brandContract.amount : 0), 0),
    jdAlliance: records.reduce((sum, record) =>
      sum + (advertisingSourceAvailable(record.jdAlliance) ? record.jdAlliance.amount : 0), 0),
    total: records.reduce((sum, record) => sum + advertisingRecordTotal(record), 0),
    provisionalDays: completeRecords.filter((record) =>
      isProvisional(record.regularJzt)
      || isProvisional(record.brandContract)
      || isProvisional(record.jdAlliance),
    ).length,
  }
}

export function summarizeAdvertisingProfit(
  data: AdvertisingCostData | null,
  dates: string[],
  gmv: number,
  profitBeforeAdvertising: number | null,
): AdvertisingProfitSummary {
  const advertising = summarizeAdvertising(data, dates)
  const profitAfterAdvertising = advertising.complete && profitBeforeAdvertising !== null
    ? profitBeforeAdvertising - advertising.total
    : null
  return {
    ...advertising,
    gmv,
    profitBeforeAdvertising,
    profitAfterAdvertising,
    advertisingRate: advertising.complete && gmv > 0 ? advertising.total / gmv : null,
    profitAfterAdvertisingMargin: profitAfterAdvertising !== null && gmv > 0
      ? profitAfterAdvertising / gmv
      : null,
  }
}
